/* eslint-disable radix */
import React, { memo, useMemo } from 'react';
import moment from 'moment';
import { get, upperCase, isEqual, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import SSPList from '../../../common/components/SSPList';
import { TableSelect } from '../../ChargersList/components/ChargersTableComponents';
import { connectorIdOptions } from '../../../common/services/ChargerFormBuilidingService';
import {
  onDatePickerFilter,
  onTextFilterNosaving as onTextFilter,
} from '../../../common/components/CommonTableDataFilter/CommonTableDataFilter';
import { iotOnDateFormat } from '../../../common/services/ValidationRules';
import { getDurationWithMainHours } from '../../../common/services/DateUtils';
import {
  buildChargingModes,
  chargingModes,
  getSessionsColumnsFromSettings,
  sessionStatusColumn,
  sessionTypeColumn,
} from '../../../common/components/CommonTableColumns';
import { setEnergyOutput } from '../../../common/resources/helper';

export const sessionsListDefaultSortedObj = { id: 'start_time', desc: true };

export const sessionsTabTableId = 'sessions';
const BASE_EXCLUDED_FIELDS = ['charger_name', 'c_uuid'];

const ChargingSessionsTab = memo(
  ({ charger, isMobile, isModal, onGetDataAction, config, onExport, exportLimit }) => {
    const hideFullEnergy = get(config, 'chargerDetails.sessions.fields', []);

    const sessionsColumns = useMemo(() => {
      const chargingModeOptions = buildChargingModes();
      const connectorIdsOptions = connectorIdOptions(charger, config);

      const baseFields = [
        {
          Header: 'Connector Name',
          id: 'connector_id',
          minWidth: 170,
          accessor: 'connector_name',
          Filter: ({ filter, onChange }) => (
            <TableSelect options={connectorIdsOptions} onChange={onChange} value={filter ? filter.value : ''} />
          ),
        },
        {
          Header: 'User IdTag',
          id: 'id_tag',
          accessor: 'id_tag',
          minWidth: 100,
          Filter: onTextFilter,
        },
        {
          Header: 'Session ID',
          id: 'id',
          minWidth: 150,
          accessor: 'session_id',
          Filter: onTextFilter,
        },
        sessionTypeColumn,
        {
          Header: 'EV ID',
          id: 'ev_id',
          minWidth: 150,
          accessor: 'ev_id',
          Filter: onTextFilter,
        },
        {
          Header: 'Charging Mode',
          id: 'charge_mode',
          minWidth: 200,
          accessor: (d) => {
            const mode = get(d, 'charge_mode');

            return mode ? get(chargingModes, upperCase(mode)) : '';
          },
          Filter: ({ filter, onChange }) => (
            <TableSelect options={chargingModeOptions} onChange={onChange} value={filter ? filter.value : ''} />
          ),
        },
        {
          Header: 'Start Time (UTC)',
          id: 'start_time',
          minWidth: 200,
          accessor: 'start_time',
          Cell: ({ value }) => moment.unix(value).utc().format(iotOnDateFormat),
          Filter: onDatePickerFilter,
        },
        {
          Header: 'End Time (UTC)',
          id: 'end_time',
          minWidth: 200,
          accessor: 'end_time',
          Cell: ({ value }) => (value ? moment.unix(value).utc().format(iotOnDateFormat) : ''),
          Filter: onDatePickerFilter,
        },
        {
          Header: 'Duration (hh:mm:ss)',
          id: 'duration',
          minWidth: 130,
          accessor: (d) => getDurationWithMainHours(get(d, 'duration', 0), true),
          filterable: false,
        },
        {
          Header: 'Energy Total (kWh)',
          accessor: 'energy',
          id: 'energy',
          Filter: onTextFilter,
          Cell: ({ value }) => setEnergyOutput(value),
          minWidth: 200,
        },
        sessionStatusColumn,
      ];

      if (!isEmpty(hideFullEnergy)) {
        baseFields.push(
          {
            Header: 'Energy Positive (kWh)',
            accessor: 'positive_energy',
            id: 'positive_energy',
            Filter: onTextFilter,
            Cell: ({ value }) => setEnergyOutput(value),
            minWidth: 200,
          },
          {
            Header: 'Energy Negative (kWh)',
            accessor: 'negative_energy',
            id: 'negative_energy',
            Cell: ({ value }) => setEnergyOutput(value),
            Filter: onTextFilter,
            minWidth: 200,
          },
        );
      }

      return baseFields;
    }, [charger, config]);

    const wrapperClassName = `create-edit-static-list${isMobile ? ' mobile' : ''}${isModal ? ' modal' : ''}`;

    return (
      <div data-activetab="sessions" className={wrapperClassName}>
        <SSPList
          exportLimit={exportLimit}
          dynamicSectionName="vendor"
          isModal={isModal}
          mobileSticky
          onGetDataAction={onGetDataAction}
          defaultPageSize={20}
          defaultSorted={sessionsListDefaultSortedObj}
          id={sessionsTabTableId}
          columns={getSessionsColumnsFromSettings(sessionsColumns, config, BASE_EXCLUDED_FIELDS)}
          clearFilterOnInit
          multiselect
          exportable
          onExport={onExport}
        />
      </div>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.isMobile, nextProps.isMobile) &&
    isEqual(prevProps.isModal, nextProps.isModal) &&
    isEqual(prevProps.charger, nextProps.charger),
);

ChargingSessionsTab.propTypes = {
  charger: PropTypes.object,
  config: PropTypes.object,
  isMobile: PropTypes.bool,
  isModal: PropTypes.bool,
  onGetDataAction: PropTypes.func,
};

export default ChargingSessionsTab;
